// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { filter, isEmpty, get, orderBy } from 'lodash/fp';
import { parse as parseQuery } from 'qs';
import styled from '@emotion/styled';

import Page from 'app/containers/parts/Page';
import {
  setLobbySubCategory,
  setLobbySport,
  setSelectedChallenge,
  startLobbyCustomPoll,
  startLobbyH2HPoll,
  startLobbyPoll,
  stopLobbyCustomPoll,
  stopLobbyH2HPoll,
  stopLobbyPoll
} from 'app/actions/lobbyActions';
import { fetchMyLiveChallenges } from 'app/actions/myChallengesActions';
import lobbyChallengesSelector from 'app/selectors/lobbyChallengesSelector';
import CustomHelmet from 'app/components/commons/CustomHelmet';
import LobbyFilterBar from 'app/components/challenges/LobbyFilterBar';
// import AdvertHandler from 'app/containers/parts/AdvertHandler';
import AdvertModal from 'app/components/commons/AdvertModal';
import Divider from 'app/components/commons/Divider';
import FriendsLoader from 'app/components/friends/common/FriendsLoader';
import CloseableInstructions from 'app/components/commons/CloseableInstructions';
import GroupedChallengesList from 'app/components/challenges/GroupedChallengesList';
import LocalStorageWrapper from 'app/components/util/LocalStorageWrapper';
import PromoBanner from 'app/components/commons/PromoBanner';
import Responsive from 'app/components/responsive/Responsive';
import SportsFilterBox from 'app/components/sportsFilters/SportsFilterBox';
import LobbyChallengeInfoModal from 'app/components/challenge/LobbyChallengeInfoModal';
import t from 'app/utils/translate';
import Button from '../components/commons/Button';
import AdImageLink from '../components/commons/AdImageLink';
import Icon from '../components/commons/Icon';
import { CUSTOM, FEATURED, H2H } from '../constants/challengeCategoryTypes';

type Props = {
  activeSport: ?string,
  activeFilter?: string,
  activeSubCategory: ?string,
  banners?: Array<*>,
  bottomAd?: Object,
  challengesData: Array<Object>,
  displayCreateH2H: boolean,
  displayAdModal: boolean,
  displayLobbyBanner: boolean,
  onFetchMyLiveChallenges: () => void,
  fetchingUserData: boolean,
  isFetching: boolean,
  isCustomFetching: boolean,
  isH2HFetching: boolean,
  isSignedIn: boolean,
  onChallengeNameClick: string => void,
  onSportSelect: (sport: string, subCategory: ?string) => void,
  onStartCustomPoll: () => void,
  onStartH2HPoll: () => void,
  onStartPoll: () => void,
  onStopCustomPoll: () => void,
  onStopH2HPoll: () => void,
  onStopPoll: () => void,
  onSubCategorySelect: string => void,
  pinnedChallengesAd?: Object,
  selectedChallengeId?: string,
  showCustom?: boolean,
  userName: string,
  htmlTitle?: string,
  description?: string
};

type State = {
  renderChallenges: boolean
};

const AdvertContainer = styled.div(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  width: '100vw',
  zIndex: theme.zIndex.sticky
}));

// const Advertisement = glamorous(AdvertHandler)(({ theme }) => ({
//   backgroundColor: theme.color.basis1,
// }));

const AdWrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.color.basis1
}));

const LobbyPage = styled(Page)({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1,
  flexBasis: 'auto'
});

const CreateContestIcon = styled(Icon)({
  fontSize: '1.4rem',
  marginLeft: '0.2rem',
  marginBottom: '0.2rem'
});

const NewLabel = styled.span(({ theme }) => ({
  color: theme.color.primary,
  backgroundColor: 'white',
  borderRadius: '3px',
  padding: '0 3px',
  marginLeft: '5px'
}));

const LoaderWrapper = styled.div({
  position: 'relative'
});

class Lobby extends React.Component<Props, State> {
  props: Props;

  state: State = {
    renderChallenges: false
  };

  static defaultProps = {
    activeFilter: undefined,
    banners: [],
    bottomAd: undefined,
    selectedChallengeId: undefined,
    htmlTitle: undefined,
    description: undefined,
    pinnedChallengesAd: undefined,
    showCustom: false
  };

  componentDidMount() {
    const { fetchingUserData, isSignedIn } = this.props;
    const { renderChallenges } = this.state;

    if (!fetchingUserData) {
      this.initialLoad(isSignedIn);
    }

    if (!renderChallenges) {
      setTimeout(() => this.setState({ renderChallenges: true }), 300);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { fetchingUserData, isSignedIn } = this.props;

    if ((fetchingUserData && !nextProps.fetchingUserData) || isSignedIn !== nextProps.isSignedIn) {
      this.initialLoad(nextProps.isSignedIn);
    }
  }

  componentWillUnmount() {
    const { onStopCustomPoll, onStopH2HPoll, onStopPoll } = this.props;
    onStopPoll();
    onStopCustomPoll();
    onStopH2HPoll();
  }

  initialLoad(isSignedIn) {
    const { onStartCustomPoll, onStartH2HPoll, onStartPoll, onFetchMyLiveChallenges } = this.props;

    onStartPoll();
    onStartH2HPoll();

    // fetch my live challenges here to alert the user they have a live challenge in the menu
    if (isSignedIn) {
      onStartCustomPoll();
      onFetchMyLiveChallenges();
    }
  }

  render() {
    const {
      activeFilter,
      activeSport,
      activeSubCategory,
      banners,
      bottomAd,
      challengesData,
      displayCreateH2H,
      displayAdModal,
      displayLobbyBanner,
      isFetching,
      isCustomFetching,
      isH2HFetching,
      isSignedIn,
      onChallengeNameClick,
      onSportSelect,
      onSubCategorySelect,
      pinnedChallengesAd,
      selectedChallengeId,
      showCustom,
      userName,
      htmlTitle,
      description
    } = this.props;

    const { renderChallenges } = this.state;
    const sortFunc = showCustom
      ? orderBy(['startTime', 'order', 'cost', 'winnings'], ['asc', 'asc', 'desc', 'desc'])
      : undefined;

    return (
      <LobbyPage title={t('Lobby')} showAd>
        <CustomHelmet description={description} htmlTitle={htmlTitle} title={t('Lobby')} />
        <Divider blank />
        <CloseableInstructions
          storageKey="HIDE_LOBBY_INSTRUCTIONS"
          title={`${t('Welcome to the Lobby')} ${userName}`}
          hasDivider
        >
          {t("Let's get started, select a challenge to enter.")}
        </CloseableInstructions>
        {displayLobbyBanner && Array.isArray(banners) && banners.length > 0 && (
          <React.Fragment>
            <PromoBanner images={banners} isSignedIn={isSignedIn} />
            <Divider blank />
          </React.Fragment>
        )}
        <SportsFilterBox
          activeSport={activeSport}
          activeFilter={activeFilter}
          activeSubCategory={activeSubCategory}
          namespace="/lobby"
          onSportSelect={onSportSelect}
          onSubCategorySelect={onSubCategorySelect}
          showAll={false}
          showFeatured
          showSubCategories
        />
        <LobbyFilterBar activeFilter={activeFilter} />
        <Divider blank />
        <Responsive mobile>
          {displayCreateH2H && (
            <Button
              style={{ textAlign: 'center' }}
              autoBorderRadius={false}
              link="/create-challenge"
            >
              Create {t('Challenge')} <CreateContestIcon type="icon-create-contest" />
              <NewLabel>NEW</NewLabel>
            </Button>
          )}
        </Responsive>
        {renderChallenges ? (
          <GroupedChallengesList
            challengesData={challengesData}
            isFetching={isFetching || isCustomFetching || isH2HFetching}
            onNameClick={onChallengeNameClick}
            pinnedChallengesAd={pinnedChallengesAd}
            showChallengeEntryState={false}
            marginBottom={bottomAd && bottomAd.enabled ? '50px' : undefined}
            sortFunc={sortFunc}
            showEnter
            showSportsIcon
          />
        ) : (
          <LoaderWrapper>
            <FriendsLoader showOverlay>{t('Loading...')}</FriendsLoader>
          </LoaderWrapper>
        )}
        <Responsive mobile={displayAdModal}>
          <LocalStorageWrapper storageKey="HIDE_AD_MODAL" storageMedium="sessionStorage">
            {({ onChange, value }) =>
              !value && <AdvertModal onClose={() => onChange('true')} zone="lobbyModal" />
            }
          </LocalStorageWrapper>
        </Responsive>
        {bottomAd && bottomAd.enabled && (
          <Responsive mobile>
            <AdvertContainer>
              {/* <Advertisement zone="lobbyFooter" /> */}
              <AdWrapper>
                <AdImageLink ad={bottomAd} />
              </AdWrapper>
            </AdvertContainer>
          </Responsive>
        )}
        {selectedChallengeId && <LobbyChallengeInfoModal />}
      </LobbyPage>
    );
  }
}

function mapStateToProps(state: GlobalState, { location }) {
  const query = parseQuery(location.search, { ignoreQueryPrefix: true });
  const {
    activeCategory,
    activeSubCategory,
    activeSport,
    challenges,
    isFetching,
    isCustomFetching,
    isH2HFetching,
    selectedChallengeId,
    customChallenges,
    h2hChallenges
  } = state.lobbyChallenges;
  const activeFilter = activeSport ? undefined : FEATURED;
  const showH2H = activeCategory === H2H && !activeFilter;
  const showCustom = activeCategory === CUSTOM && !activeFilter;
  const { isSignedIn } = state.user;
  const { promo = [] } = state.settings;
  let selectedChallenges = challenges;

  if (showH2H) {
    selectedChallenges = h2hChallenges;
  } else if (showCustom) {
    selectedChallenges = customChallenges;
  }

  return {
    activeSport,
    activeFilter,
    activeSubCategory,
    banners: isSignedIn ? promo : filter('isPublic')(promo),
    bottomAd: get('settings.kayoAds.bottomAd', state),
    challengesData: lobbyChallengesSelector({
      ...state,
      lobbyChallenges: { ...state.lobbyChallenges, challenges: selectedChallenges },
      activeFilter
    }),
    displayAdModal: !query.modal && !isEmpty(get('lobbyModal', state.settings.doubleClick)),
    displayLobbyBanner: state.settings.displayLobbyBanner,
    displayCreateH2H: state.settings.displayCreateH2H,
    fetchingUserData: state.user.fetchingUserData,
    isFetching,
    isCustomFetching,
    isH2HFetching,
    isSignedIn,
    pinnedChallengesAd: get('settings.kayoAds.lobbyPinnedChallengesAd', state),
    selectedChallengeId,
    userName: get('name', state.user.currentUser) || '',
    htmlTitle: get('content.lobby.htmlTitle', state.settings),
    description: get('content.lobby.description', state.settings),
    showCustom
  };
}

function mapDispatchToProps(dispatch) {
  return {
    onFetchMyLiveChallenges() {
      dispatch(fetchMyLiveChallenges());
    },
    onChallengeNameClick(id: string) {
      dispatch(setSelectedChallenge(id));
    },
    onSportSelect(sport: string, subCategory: ?string) {
      dispatch(setLobbySport(sport, subCategory));
    },
    onSubCategorySelect(sport: string) {
      dispatch(setLobbySubCategory(sport));
    },
    onStartCustomPoll() {
      dispatch(startLobbyCustomPoll(true));
    },
    onStartH2HPoll() {
      dispatch(startLobbyH2HPoll(true));
    },
    onStartPoll() {
      dispatch(startLobbyPoll(true));
    },
    onStopPoll() {
      dispatch(stopLobbyPoll());
    },
    onStopCustomPoll() {
      dispatch(stopLobbyCustomPoll());
    },
    onStopH2HPoll() {
      dispatch(stopLobbyH2HPoll());
    }
  };
}

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(Lobby);
